import { NgForOf } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { BaseComponent } from '../../shared/base.component';
import { ViewDealersState } from '../../shared/state-interface';
import { DealerNamesComponent } from '../../ui-components/dealer-names/dealer-names.component';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { Dealer } from '../shared/dealer.model';
import { DealerService } from '../shared/dealer.service';

@Component({
    selector: 'app-view-dealers',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatExpansionModule,
        MatTableModule,
        MatIconModule,
        MatMenuModule,
        MatPaginatorModule,
        NgForOf,
        SiteHeaderComponent,
        DealerNamesComponent
    ],
    templateUrl: './view-dealers.component.html',
    styleUrl: './view-dealers.component.css'
})
export class ViewDealersComponent extends BaseComponent implements AfterViewInit {

    @ViewChild(DealerNamesComponent) dealerNamesComponent!:DealerNamesComponent;
    @Output() messageEvent = new EventEmitter<string>();

    private dealerService: DealerService = inject(DealerService);

    private static _retrievingDealers = "Retrieving Dealers";
    private static _retrievingDealersError = `${ViewDealersComponent._retrievingDealers} error:`;
    private static _inactivatingDealer = "Inactivating Dealer";
    private static _inactivatingDealerError = `${ViewDealersComponent._inactivatingDealer} error:`;
    private static _reactivatingDealer = "Reactivating Dealer";
    private static _reactivatingDealerError = `${ViewDealersComponent._reactivatingDealer} error:`;
    private static _deletingDealer = "Deleting Dealer";
    private static _deletingDealerError = `${ViewDealersComponent._deletingDealer} error:`;

    protected viewDealersForm = new FormGroup({
        contactLastName: new FormControl(""),
        contactFirstName: new FormControl(""),
        contactEmail: new FormControl("")
    });

    private _dealers: WritableSignal<Dealer[]> = signal<Dealer[]>([]);
    private _dealerCount: WritableSignal<number> = signal<number>(-1);

    public ngAfterViewInit() {
        const state: ViewDealersState | undefined = this.stateManager.viewDealersState;

        if (state != undefined) {
            this.viewDealersForm = state.viewDealersForm;
            this.dealerNamesComponent.restoreState(state.dealerNamesComponentState!);

            if (this.refresh) {
                this.pageIndex = 0;
                this.dealerCount = -1;
                this.listDealers(this.overrideSnackBar);
            } else {
                this.dealers = state.dealers;
                this.dealerCount = state.dealerCount;
                super.restoreState(state.baseComponentState);
            }
        } else {
            this.dealerNamesComponent.init();
        }
    }

    public ngOnDestroy() {
        const state: ViewDealersState = {} as ViewDealersState;
        state.viewDealersForm = this.viewDealersForm;
        state.dealers = this.dealers;
        state.dealerCount = this.dealerCount;
        state.dealerNamesComponentState = this.dealerNamesComponent.getState();
        state.baseComponentState = super.getState();
        this.stateManager.viewDealersState = state;
    }

    protected getAltCssClass(className: string): string {
        return (this.userType != "employee") ? className + "-alt" : "";
    }

    protected get dealers(): Dealer[] {
        return this._dealers();
    }

    protected get dealerCount(): number {
        return this._dealerCount();
    }

    protected override handlePageEvent(e: PageEvent): void {
        super.handlePageEvent(e);
        this.listDealers();
    }

    protected async listDealers(): Promise<void>;
    protected async listDealers(overrideSnackBar: string | undefined): Promise<void>;
    protected async listDealers(overrideSnackBar?: string): Promise<void> {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            if (this.searchParamsDirty()) {
                this.pageIndex = 0;
                this.dealerCount = -1;
                this.markSearchParams(super.pristine);
            }

            const observable = await this.dealerService.getDealers(await getIdToken(currentUser),
                this.getSearchParams(), this.pageSize, this.pageIndex);

            if (overrideSnackBar == undefined) {
                super.showSnackBar(`${ViewDealersComponent._retrievingDealers}...`, -1)
            }

            super.message = "";

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-dealers"] == "success") {
                        // this.dealers is a signal. The view will be refreshed
                        // upon update to the signal.
                        this.dealers = Dealer.toDealers(json["dealers"]);
                        this.dealerCount = json["dealer-count"];
                        this.message = "";
                        overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar);
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    const message = `${ViewDealersComponent._retrievingDealersError} ${(<Error>error).message}`;
                    super.message = message;
                }
            })
        } else {
            const message = `${ViewDealersComponent._retrievingDealersError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async resetSearch() {
        this.dealerNamesComponent.dealerCtl.setValue("");
        this.contactLastNameCtl.setValue("");
        this.contactFirstNameCtl.setValue("");
        this.contactEmailCtl.setValue("");
        this.markSearchParams(super.dirty);
        this.listDealers();
    }

    protected async addDealer() {
        this.stateManager.addDealerState = undefined;
        this.router.navigate(["/add-dealer"]);
    }

    protected async updateDealer(id: number) {
        this.stateManager.updateDealerState = undefined;
        this.router.navigate([`/update-dealer/${id}`]);
    }
    
    protected async inactivateDealer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerService.inactivateDealer(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDealersComponent._inactivatingDealer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["inactivate-dealer"] == "success") {
                        this.stateManager.clearDealerNames();
                        this.listDealers("Dealer inactivated");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDealersComponent._inactivatingDealerError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDealersComponent._inactivatingDealerError} Null user instance`;
            console.error(message);
            super.showSnackBar(message, 30);
        }
    }

    protected async reactivateDealer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerService.reactivateDealer(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDealersComponent._reactivatingDealer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["reactivate-dealer"] == "success") {
                        this.stateManager.clearDealerNames();
                        this.listDealers("Dealer Reactivated");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDealersComponent._reactivatingDealerError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDealersComponent._reactivatingDealerError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async deleteDealer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerService.deleteDealer(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDealersComponent._deletingDealer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["delete-dealer"] == "success") {
                        if (this.dealers.length == 1) {
                            // Nothing left on current page, backup
                            // one page.
                            this.pageIndex -= 1;
                        }
                        this.stateManager.clearDealerNames();
                        this.listDealers("Dealer deleted");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDealersComponent._deletingDealerError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDealersComponent._deletingDealerError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected searchParamsDirty(): boolean {
        return this.dealerNamesComponent.dealerCtl.dirty
            || this.contactLastNameCtl.dirty
            || this.contactFirstNameCtl.dirty
            || this.contactEmailCtl.dirty;
    }

    protected markSearchParams(func: Function) {
        super.markControl(this.dealerNamesComponent.dealerCtl, func);
        super.markControl(this.contactLastNameCtl, func);
        super.markControl(this.contactFirstNameCtl, func);
        super.markControl(this.contactEmailCtl, func);
    }

    private getSearchParams(): any {
        return {
            "dealerId": (this.dealerNamesComponent.selectedDealer > 0 ?
                            this.dealerNamesComponent.selectedDealer : undefined),
            "contactLastName": this.contactLastNameCtl.value,
            "contactFirstName": this.contactFirstNameCtl.value,
            "contactEmail": this.contactEmailCtl.value
        }
    }

    private get contactLastNameCtl(): FormControl {
        return this.viewDealersForm.controls.contactLastName;
    }

    private get contactFirstNameCtl(): FormControl {
        return this.viewDealersForm.controls.contactFirstName;
    }

    private get contactEmailCtl(): FormControl {
        return this.viewDealersForm.controls.contactEmail;
    }

    private set dealers(value: Dealer[]) {
        this._dealers.set(value);
    }

    private set dealerCount(value: number) {
        this._dealerCount.set(value);
    }
}
